import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';


import Chip from '@mui/joy/Chip';
import Skeleton from '@mui/joy/Skeleton';

import Tooltip from '@mui/joy/Tooltip';
import Button from '@mui/joy/Button';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemDecorator from '@mui/joy/ListItemDecorator';
import ListItemContent from '@mui/joy/ListItemContent';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';


import useUserIdentity from 'hooks/useUserIdentity';
import useFHRequest from 'hooks/useFHRequest';
import useRoles from 'hooks/useRoles';
import useUser from 'hooks/useUser';
import useWorkKey from 'hooks/useWorkKey';

import CollabWorkSpaceSidebarCallLog from 'components/CollabWorkSpaceSidebarCallLog';
import CollabWorkSpaceSidebarEmail from 'components/CollabWorkSpaceSidebarEmail';
import CallIncomingListByIncomingPhoneNumber from 'components/CallIncomingListByIncomingPhoneNumber';



import AssociatedTools from 'components/AssociatedTools';
import { DateOrTime } from 'components/Date';
import LockedFeature from 'components/LockedFeature';
import { Success } from 'components/FancyToast';

import RequestDelayList from 'components/RequestDelayList';
import CollabWorkSpaceSidebarFiles from 'components/CollabWorkSpaceSidebarFiles';
import CollabClockTower from 'components/CollabClockTower';
import SidebarIdentityInfo from 'components/SidebarIdentityInfo';
import SidebarInfo from 'components/SidebarInfo';
import ClockTotalSidebar from 'components/ClockTotalSidebar';

import { FHRequestType } from 'stores/fhrequest_store';
import { UserProps } from 'stores/user_store';

import { pluralize, REQUEST_STATUS } from 'utils/misc';
import { getIcon } from 'utils/misc';
import { phoneFormat } from 'utils/misc';
import { money } from 'utils/misc';
import { REQUEST_SOURCE } from 'utils/misc';
import IdentityAvatar from './IdentityAvatar';

import RequestInfoEditCredits from 'components/RequestInfoEditCredits';

import RequestSidebarRequestAttachments from 'components/RequestSidebarRequestAttachments';
import useClock from 'hooks/useClock';

import RequestSidebarSupportTeam from 'components/RequestSidebarSupportTeam';

import { FHRequestAttachmentProvider } from 'contexts/fhrequest_attachment_context';
import useIdentity from 'hooks/useIdentity';
import RequestInfoUpdateStatus from './RequestInfoUpdateStatus';


const RequestSidebar = observer(({ request }: { request: FHRequestType }) => {
    const { currentIdentity } = useUserIdentity();

    // console.log("request.needs_manager_attention", request.needs_manager_attention);

    if (!request?.key) return;
    if (!currentIdentity) return null;

    return (
        <Stack

            sx={() => ({
                zIndex: 850,
                position: 'sticky',
                overflowY: 'auto',
                top: "var(--TopperHeight)",
            })}

        >
            <RequestSidebarInfo request={request} />

            <ClockTotalSidebar
                total_seconds={request.clock_total_seconds} />

        </Stack>
    );
});



const RequestUserInfo = observer(({ request }: { request: FHRequestType }) => {
    const { fetchUserInfoRequest } = useFHRequest();
    const [user, setUser] = useState({} as UserProps);

    const [loaded, setLoaded] = useState(false);
    const { hasRole } = useRoles();
    const EmailIcon = getIcon('MailOutline');

    useEffect(() => {
        if (loaded) return;
        fetchUserInfoRequest(request.uuid || request.key)
            .then((user) => {
                user && setUser(user);
                setLoaded(true);
                console.log("user,", user);
            });
    }, [request.uuid, fetchUserInfoRequest]);

    return (
        <Stack
            spacing={1}
            alignItems="center"
        >

            <Stack alignItems="center">
                <Typography level="body-xs">
                    Client
                </Typography>
                {!loaded ?
                    <Skeleton width={85} variant="text" />
                    :
                    <Typography level="title-sm">
                        {user.first_name} {user.last_name}
                    </Typography>
                }
            </Stack>

            <LockedFeature
                lockedIconName="PersonPin"
                unlockedIconName="LockPerson" outlined={false}
                copy="Show Client Info">
                {loaded &&
                    <Stack spacing={1}>
                        <Typography
                            sx={{ textTransform: 'uppercase' }}
                            level="body-xs">Email</Typography>

                        <Tooltip arrow title="Copy to clipboard">
                            <Typography
                                mx={0.5}
                                sx={{ cursor: 'default' }}
                                level="body-sm"
                                color="primary"
                                startDecorator={<EmailIcon />}
                                onClick={() => {
                                    navigator.clipboard.writeText(user.primary_email);
                                    Success("Copied to clipboard");
                                }}>
                                {user.primary_email}

                            </Typography>
                        </Tooltip>

                        {user.phone_number &&
                            <>
                                <Typography
                                    sx={{ textTransform: 'uppercase' }}
                                    level="body-xs">Phone</Typography>
                                <Typography
                                    level="body-xs">
                                    {phoneFormat(user.phone_number)}
                                </Typography>
                            </>
                        }

                        <Typography
                            sx={{ textTransform: 'uppercase' }}
                            level="body-xs">
                            Home Address
                        </Typography>
                        {user.home_street_1 &&
                            <Typography
                                level="body-sm">
                                {user.home_street_1}
                            </Typography>
                        }
                        {user.home_street_2 &&
                            <Typography
                                level="body-sm">
                                {user.home_street_2}
                            </Typography>
                        }
                        <Typography
                            level="body-sm">
                            {user.home_city} {user.home_state} {user.home_zip}
                        </Typography>
                        {hasRole('support') &&

                            <Stack>
                                <Typography
                                    sx={{ textTransform: 'uppercase' }}
                                    level="body-xs">
                                    SUPPORT INFO
                                </Typography>

                                <Typography level="body-xs"
                                    endDecorator={<Chip>{user.tasks_remaining}</Chip>}>
                                    Tasks Remaining
                                </Typography>
                            </Stack>

                        }
                    </Stack>
                }
            </LockedFeature>
        </Stack>
    );
});

const RequestInfoListItem = ({ title, value, icon, onEdit = undefined }: { title: string, value: string | React.ReactNode, icon: string, onEdit?: () => void }) => {
    const Icon = getIcon(icon);
    const EditIcon = getIcon('Edit');
    const color = "neutral";


    return (
        <ListItem>
            <ListItemDecorator>
                <Icon color={color} />
            </ListItemDecorator>

            <ListItemContent>
                <Typography
                    level="body-sm"
                    endDecorator={onEdit ?
                        <Tooltip arrow
                            title={`Edit ${title}`}><EditIcon onClick={onEdit} />
                        </Tooltip>
                        :
                        null}
                >{title}</Typography>
            </ListItemContent>
            <Typography level="body-sm">
                {value}
            </Typography>
        </ListItem>
    );
};


const PersonAssigner = ({onChange}: {onChange: (s: string) => void}) => {
    const [people, setPeople] = useState([] as UserProps[]);
    const workKey = useWorkKey();
    const { supportAssignableUsers } = useUser();
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (!workKey) return;
        if(loaded) return;
        supportAssignableUsers(workKey)
            .then((users) => {
                setLoaded(false);
                setPeople(users);
            });
    }, []);

    return (
            <Select
                defaultValue={"unassigned"}
                onChange={(_, v) => onChange && onChange(v as string)}
                placeholder="Select one...">
                <Option value="unassigned">Unassigned</Option>

                {people.map((person) => {
                    return (
                        <Option
                            key={person.key}
                            value={person.key}>
                            { person.verified_uuid  ?
                                <IdentityAvatar interactive={false} size="inline" identityKey={person.verified_uuid} /> :
                                <Typography>{person.first_name} {person.last_name} </Typography> }
                        </Option>
                    );
                })}
            </Select>
    );
};

export const RequestSidebarAssigner = ({ request, setEditing }: { request: FHRequestType, setEditing: (s: boolean) => void }) => {

    const [assignedUser, setAssignedUser] = useState(request.claimed_by);

    const workKey = useWorkKey();
    const { supportAssign } = useFHRequest();

    const doAssign = () => {
        if (!workKey) return;
        supportAssign(workKey, assignedUser, request.uuid)
            .then(() => {
                setEditing(false);
                Success("Assigned");
            });
    };

    return (
        <Stack
            spacing={1}
            alignItems="center"
        >
            <Typography level="title-sm">
                Select an assistant
            </Typography>

            <PersonAssigner onChange={(s) => setAssignedUser(s)} />

            {assignedUser &&
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="outlined"
                        onClick={doAssign}
                    >Assign</Button>
                </Stack>
            }
        </Stack>
    );
};



const RequestInfoAssistant = ({ request, clockedIn }: { request: FHRequestType, clockedIn: boolean }) => {
    const { hasRole } = useRoles();
    const EditIcon = getIcon('Edit');
    const [editing, setEditing] = useState(false);

    if (request.claimed_identity) {
        return (
            <Stack alignItems="center"
                spacing={0.3}
                sx={{ '--Avatar-size': '16px' }} >
                <Typography
                    endDecorator={hasRole('support') && clockedIn ?
                        <EditIcon onClick={() => setEditing(!editing)} />
                        :
                        null}
                    level="body-xs">
                    Assistant
                </Typography>
                <Typography
                    level="body-sm">
                    <IdentityAvatar
                        interactive="link"
                        size="inline"
                        identityKey={request.claimed_identity} />
                </Typography>
                {editing &&
                    <RequestSidebarAssigner
                        request={request}
                        setEditing={setEditing} />
                }
            </Stack>
        );
    }

    return null;

};



const RequestInfoList = observer(({ request, clockedIn, editing, setEditing }: { request: FHRequestType, clockedIn: boolean, editing: string, setEditing: (s: string) => void }) => {
    const SunriseIcon = getIcon('Sunrise');
    const ContentCopy = getIcon('ContentCopy');
    const { currentIdentity } = useUserIdentity();
    const { getIdentity } = useIdentity();
    // const { currentClock } = useClock()
    const { hasRole } = useRoles();
    const [requestIdentity, _] = useState(getIdentity(request.identity));

    const StorageIcon = getIcon('Storage');
    const WebIcon = getIcon('Web');


    return (
        <Stack
            alignItems="center"
            spacing={3}
        >
            <LockedFeature copy="Request Details" outlined={false}
                lockedIconName="DnsOutlined"
                unlockedIconName="Dns"
            >

                <Stack>

                    {editing == "credits" &&
                        <RequestInfoEditCredits
                            request={request}
                            setEditing={setEditing} />
                }
                { editing == "status" &&
                    <RequestInfoUpdateStatus
                        setEditing={setEditing}
                        request={request} />
                }
                    {/* START INFO LIST: Only show if editing == '' */}
                    <List
                        sx={{ display: (editing ? 'none' : 'block') }}
                        size="sm">

                        <RequestInfoListItem
                            title="Status"
                                      value={REQUEST_STATUS.NAME(request.status)}
                            onEdit={clockedIn && hasRole('support') ? () => setEditing("status") : undefined}
                            icon="TrafficOutlined" />

                        <RequestInfoListItem
                            title="Credits"
                            value={
                                <Tooltip arrow
                                    title={
                                        (hasRole('support') && requestIdentity?.uuid) ?
                                            `User has ${pluralize(requestIdentity?.tasks_remaining || 0, 'task')} remaining` : null
                                    }
                                >
                                    <Chip>{request.task_credits}</Chip>
                                </Tooltip>
                            }
                            onEdit={clockedIn && hasRole('support') ? () => setEditing("credits") : undefined}
                            icon="ReceiptOutlined" />

                        {((currentIdentity.role == 'assistant') &&
                            !request.pay_via_shift)
                            &&
                            <RequestInfoListItem
                                title="Price"
                                value={
                                    <Tooltip arrow
                                        title="Price is not shown to client.">
                                        <Chip color="success" variant="outlined">{money(request.price)}</Chip>
                                    </Tooltip>
                                }
                                icon="LocalAtm" />
                        }

                        <RequestInfoListItem
                            title="Source"
                            icon="SourceOutlined"
                            value={<Tooltip arrow
                                title="Where did this task come from?">
                                <Chip
                                    startDecorator={request.source > 20 ? <SunriseIcon color="success" /> : null}
                                    color="success"
                                    size="sm">
                                    {REQUEST_SOURCE.NAME(request.source)}
                                </Chip>
                            </Tooltip>
                            }
                        />


                        <RequestInfoListItem
                            title="Created"
                            icon="DateRange"
                            value={<DateOrTime ts={request.date_created} />}
                        />

                        <RequestInfoListItem
                            title="Email"
                            icon="AlternateEmailOutlined"
                            value={<Tooltip
                                variant="outlined"
                                arrow
                                title={
                                    <Stack
                                        alignItems="center"
                                        m={3}
                                        spacing={1}>
                                        <Typography>
                                            Email to this address will end up on this page
                                        </Typography>
                                        <Chip
                                            startDecorator={<ContentCopy />}
                                            onClick={() => {
                                                navigator.clipboard.writeText(`request-${request.uniq}@email.fhands.com`)
                                                Success("Copied email to clipboard");
                                            }}
                                            size="sm">request-{request.uniq}@email.fhands.com</Chip>
                                    </Stack>}>
                                <Chip size="sm">request-{request.uniq}@{String.fromCodePoint(8230)}</Chip>
                            </Tooltip>
                            }
                        />

                    </List>


                    {/* END INFO LIST */}

                </Stack>


                <SidebarInfo
                    parent_type="request"
                    parent_uuid={request.uuid} />

                <CollabWorkSpaceSidebarFiles
                    type="request"
                    type_uuid={request.uuid} />

                {/* legacy attachments */}
                <RequestSidebarRequestAttachments
                    request={request} />

                <CollabWorkSpaceSidebarEmail
                    associated_type="request"
                    associated_uuid={request.uuid} />

                <CollabWorkSpaceSidebarCallLog
                    associated_type="request"
                    associated_uuid={request.uuid} />

                <CallIncomingListByIncomingPhoneNumber
                    associated_type="request"
                    associated_uuid={request.uuid} />

                <RequestDelayList
                    request={request} />
                <CollabClockTower
                    associated_type="request"
                    associated_uuid={request.uuid} />

                    <Stack mt={2} spacing={1} alignItems="center">
                    {hasRole('support') &&
                    <a
                        target="_blank"
                        href={`https://www.fancyhands.com/admin/request/${request.uniq}`}>
                        <Typography
                            startDecorator={<WebIcon />}
                            color="primary"

                            level="body-sm">
                            View on fancyhands.com
                        </Typography>
                        </a>}

                    {hasRole('sudo') &&
                        <a
                            target="_blank"

                            href={`https://console.cloud.google.com/datastore/entities/edit?project=fancyhands-hrd&key=${request.ogkey}`}>
                            <Typography
                                startDecorator={<StorageIcon />}
                                color="danger"
                                level="body-sm">

                                View in Datastore
                            </Typography>
                        </a>
                    }
                </Stack>


            </LockedFeature>

        </Stack>

    );
});


const RequestSidebarInfo = ({ request }: { request: FHRequestType }) => {
    const [editing, setEditing] = useState("");
    const { hasRole } = useRoles();
    const { currentClock } = useClock();
    const [clockedIn, setClockedIn] = useState(false);


    useEffect(() => {
        if (!currentClock?.associated_uuid)
            setClockedIn(false);
        else {
            setClockedIn(currentClock.associated_uuid == request.key);
        }
    }, [currentClock?.associated_uuid, request.key]);

    return (
        <Stack
            spacing={1}
            m={2}
            mt={0}
        >

            {request.identity &&
                <SidebarIdentityInfo identityKey={request.identity} />
            }

            {!request.identity &&
                <RequestUserInfo request={request} />
            }

            {/* ASSISTANT */}
            <RequestInfoAssistant clockedIn={clockedIn} request={request} />


            <Stack spacing={1} pt={2} >

                <RequestInfoList
                    clockedIn={clockedIn}
                    editing={editing}
                    setEditing={setEditing}
                    request={request} />

                {clockedIn &&
                    <AssociatedTools
                        associated_type={"request"}
                        associated_uuid={request.uuid}
                        from_email={'request-' + request.uniq + '@email.fhands.com'}
                    />
                }

                {hasRole('support') &&
                    <LockedFeature
                        color="success"
                        copy="Support Actions"
                        lockedIconName="SupportAgent"
                        unlockedIconName="SupportAgent"
                    >
                        <Stack>


                            <RequestSidebarSupportTeam
                                clockedIn={clockedIn}
                                setEditing={setEditing}
                                editing={editing === "support"}
                                request={request} />


                        </Stack>
                    </LockedFeature>
                }

            </Stack>
        </Stack>
    );
};

export const RequestSidebarFullPage = () => {
    const { requestKey = "" } = useParams<{ requestKey: string }>();
    const { fetchRequest, getRequest } = useFHRequest();
    const navigate = useNavigate();
    const ArrowBack = getIcon('ArrowBack');
    const [request, setRequest] = useState(getRequest(requestKey));
    const [fetched, setFetched] = useState(false);

    useEffect(() => {
        if (fetched) return;
        setFetched(true);
        fetchRequest(requestKey);

    }, [fetchRequest, fetched, setFetched, requestKey, setRequest]);

    useEffect(() => {
        if (request?.title)
            document.title = request.title + " (sidebar)";
    }, [request?.title]);

    if (!request)
        return null;

    return (
        <FHRequestAttachmentProvider>
            <Stack>
                <Stack
                    m={2}
                    justifyContent="flex-start"
                    direction="row"
                >
                    <Button
                        startDecorator={<ArrowBack />}
                        variant="plain"
                        onClick={() => navigate(`/request/${requestKey}`)}
                    >View Request</Button>
                </Stack>
                <RequestSidebar
                    request={request}
                />
            </Stack>
        </FHRequestAttachmentProvider>
    );
};

export default RequestSidebar;
